// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import type { SortDir } from "./table-query.js";

export interface TableColumn {
  /** Server sort key — must appear in this page's entry in server-keys.json. */
  key: string;
  label: string;
  align?: "left" | "right" | "center";
}

/**
 * ⚠️  DECLARE EVERY CONFIG `as const satisfies TableConfig` — NEVER `: TableConfig`.
 *
 *     const FOO_TABLE = { … } as const satisfies TableConfig;   // ✅
 *     const FOO_TABLE: TableConfig = { … };                     // ❌ reopens a type hole
 *
 * A `: TableConfig` annotation still COMPILES, so the mistake is easy to make
 * and hard to see. It widens `facetKeys` from a literal tuple to `string[]`,
 * which collapses `FacetKeysOf<>` to bare `string` — and a page can then pass
 * a misspelled facet key, or one the API never implemented, with no complaint
 * until `config-parity.test.ts` runs.
 *
 * Worse than silence: the annotated form reports the SAME single unlocalized
 * error whether a facet key is typo'd or not, while the two errors that would
 * have pinpointed the typo disappear. An author sees an error and goes hunting
 * in the wrong file. `as const satisfies` keeps the literal types, so the typo
 * itself is what the compiler names.
 */
export interface TableConfig<K extends string = string> {
  defaultSort: string;
  defaultDir: SortDir;
  columns: readonly TableColumn[];
  /** Facet param names — must appear in this page's entry in server-keys.json. */
  facetKeys: readonly K[];
}

/**
 * The facet-key UNION of a config, e.g.
 * `FacetKeysOf<typeof CUSTOMERS_TABLE>` = `"reseller_id" | "business_type" | …`.
 *
 * This is why every config below is declared `as const satisfies TableConfig`
 * rather than annotated `: TableConfig`. An annotation would widen `facetKeys`
 * to `string[]` and the union would collapse to `string`, at which point a
 * page could pass a misspelled facet key — or one the API never implemented —
 * and nothing would complain until `config-parity.test.ts` ran. With the
 * literal types preserved, the page derives its facet-key type FROM the config
 * and the mismatch is a type error instead.
 */
export type FacetKeysOf<T extends TableConfig<string>> = T["facetKeys"][number];

/**
 * One config per CRM list page: which columns sort and which facets filter.
 *
 * These keys are HALF of a contract — the other half is the controller's
 * `sortable_columns` / `filterable` declaration. `config-parity.test.ts`
 * fails if they drift, because a header wired to a key the server does not
 * allow-list renders a sort arrow that silently does nothing.
 */
export const CUSTOMERS_TABLE = {
  defaultSort: "company",
  defaultDir: "asc",
  columns: [
    { key: "code", label: "Code" },
    { key: "company", label: "Company" },
    { key: "business_type", label: "Business Type" },
    { key: "reseller", label: "Reseller" },
    { key: "support_level", label: "Support Level" },
    { key: "salesperson", label: "Salesperson" },
    { key: "industry", label: "Industry" },
    { key: "next_renewal", label: "Next Renewal" },
  ],
  facetKeys: ["reseller_id", "business_type", "support_level", "industry", "salesperson_id"],
} as const satisfies TableConfig;

export const PROSPECTS_TABLE = {
  defaultSort: "company",
  defaultDir: "asc",
  columns: [
    { key: "code", label: "Code" },
    { key: "company", label: "Company" },
    { key: "industry", label: "Industry" },
    { key: "reseller", label: "Reseller" },
    { key: "salesperson", label: "Salesperson" },
    { key: "priority", label: "Priority" },
    { key: "value", label: "Value", align: "right" },
    { key: "created_at", label: "Created" },
  ],
  facetKeys: ["reseller_id", "industry", "salesperson_id", "priority", "prospect_source"],
} as const satisfies TableConfig;

export const CONTACTS_TABLE = {
  defaultSort: "name",
  defaultDir: "asc",
  columns: [
    { key: "name", label: "Name" },
    { key: "company", label: "Company" },
    { key: "industry", label: "Industry" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "job_title", label: "Job Title" },
    { key: "created_by", label: "Created By" },
    { key: "status", label: "Status" },
  ],
  facetKeys: ["industry", "status", "created_by_id"],
} as const satisfies TableConfig;

export const CONTACT_GROUPS_TABLE = {
  defaultSort: "name",
  defaultDir: "asc",
  columns: [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    { key: "members", label: "Members", align: "right" },
    { key: "owner", label: "Owner" },
    { key: "created_at", label: "Created" },
  ],
  facetKeys: ["owner_id"],
} as const satisfies TableConfig;

/**
 * Invoices. The column ORDER here is the order both surfaces render — the
 * inline table in `pages/invoices/InvoicesPage.tsx` and the shared
 * `components/document-list-table.tsx` — so the two must be edited together.
 *
 * `invoice_number` leads: both tables have rendered an "Invoice #" / "Quote #"
 * header all along, and the server has allow-listed the column all along, but
 * the config omitted it — so the header could not sort. `total_amount`
 * deliberately carries NO `align`, because the Amount BODY cells on both
 * surfaces are left-aligned; a right-aligned header over left-aligned money
 * reads as a layout bug.
 */
export const INVOICES_TABLE = {
  defaultSort: "created_at",
  defaultDir: "desc",
  columns: [
    { key: "invoice_number", label: "Invoice #" },
    { key: "customer", label: "Customer" },
    { key: "total_amount", label: "Amount" },
    { key: "payment_status", label: "Status" },
    { key: "due_date", label: "Due Date" },
    { key: "paid_date", label: "Paid Date" },
    { key: "created_at", label: "Created" },
  ],
  facetKeys: ["payment_status", "created_by_id"],
} as const satisfies TableConfig;

/**
 * Quotes are invoices with `document_type: "quote"` — ONE controller serves
 * both — so this spreads the invoices config rather than restating it.
 *
 * Two columns differ, and both differ because the CELL differs:
 *   * "Quote #" is what the header reads (same `invoice_number` column).
 *   * Status renders the QUOTE lifecycle (`invoices.status` —
 *     pending/expired/converted/rejected) rather than `payment_status`.
 *     `document-list-table`'s `getStatusBadge` branches on `isQuote` to do it.
 *     Sorting has to follow the cell, so the quotes Status header is keyed
 *     `status`, not `payment_status`. Verified against `Invoice`'s
 *     `validates :status, inclusion: { in: %w[pending expired converted rejected] }`.
 *
 * And one FACET is dropped for the same reason. `payment_status` appears in no
 * cell on this page, so filtering by it is a control whose effect the operator
 * cannot see — and every quote the UI can produce carries the `unpaid` DB
 * default (no quote surface offers a payment action), so three of the four
 * options matched nothing and the fourth matched everything: a dead control,
 * the same defect class as a header that looks clickable and does not sort.
 *
 * The SERVER still declares the facet for this endpoint (one controller, and
 * the Invoices page needs it), so `server-keys.json` is unchanged and
 * `config-parity.test.ts` — client keys must be a SUBSET of the server's —
 * still holds. Dropping it here is not enough on its own: `TableFilterBar`
 * renders whatever `facetDefs` it is handed, so `buildInvoiceFacetDefs` takes
 * the page's `facetKeys` too. Pinned by `invoice-facets.test.ts`.
 */
export const QUOTES_TABLE = {
  ...INVOICES_TABLE,
  facetKeys: ["created_by_id"],
  columns: [
    { key: "invoice_number", label: "Quote #" },
    { key: "customer", label: "Customer" },
    { key: "total_amount", label: "Amount" },
    { key: "status", label: "Status" },
    { key: "due_date", label: "Due Date" },
    { key: "paid_date", label: "Paid Date" },
    { key: "created_at", label: "Created" },
  ],
} as const satisfies TableConfig;

export const TASKS_TABLE = {
  defaultSort: "due_date",
  defaultDir: "asc",
  columns: [
    { key: "name", label: "Name" },
    { key: "due_date", label: "Due Date" },
    { key: "priority", label: "Priority" },
    { key: "status", label: "Status" },
    { key: "assigned_to", label: "Assignee" },
    { key: "created_at", label: "Created" },
  ],
  facetKeys: [],
} as const satisfies TableConfig;

export const FILES_TABLE = {
  defaultSort: "modified",
  defaultDir: "desc",
  columns: [
    { key: "name", label: "Name" },
    { key: "size", label: "Size", align: "right" },
    { key: "modified", label: "Modified" },
    { key: "created_by", label: "Created By" },
  ],
  facetKeys: ["file_type", "created_by_id"],
} as const satisfies TableConfig;

export const RESELLERS_TABLE = {
  defaultSort: "name",
  defaultDir: "asc",
  columns: [
    { key: "name", label: "Reseller" },
    { key: "code", label: "Code" },
    { key: "salespeople", label: "Assigned Salespeople" },
    { key: "dealers", label: "Dealers", align: "right" },
    { key: "location", label: "Location" },
    { key: "created_by", label: "Created By" },
    { key: "created_at", label: "Created" },
  ],
  facetKeys: ["salesperson_id", "country"],
} as const satisfies TableConfig;
